import { Link } from "react-router-dom";
import { Pill, Activity, Flame, Target, CalendarDays, Layers, Save, Users, Coins } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { missedMedIds } from "../lib/sr";
import { WeeklyRecap } from "../components/WeeklyRecap";
import { ModalitySwitch } from "../components/ModalitySwitch";
import { MODALITIES, useModality } from "../context/ModalityContext";

export default function Dashboard() {
  const { user } = useAuth();
  const { progress, isGuest } = useProgress();
  const { modality, isStudy } = useModality();
  const current = MODALITIES.find((m) => m.id === modality);
  const streak = progress?.stats?.streak || 0;
  const bestMed = progress?.stats?.med_best_score || 0;
  const highestLevel = progress?.stats?.ekg_highest_level || 1;
  const missedCount = missedMedIds(progress?.medication).size + (progress?.stats?.missed_rhythms || []).length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// {isGuest ? "GUEST SESSION" : "WELCOME BACK"}</div>
        <h1 className="text-4xl font-extrabold tracking-tight">
          Hello, <span className="text-phosphor">{user?.name || "Clinician"}</span>
        </h1>
      </div>

      {isGuest && (
        <div className="panel p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 border-amber-400/40" data-testid="guest-banner">
          <Save className="w-6 h-6 text-amber-300 shrink-0" />
          <div className="flex-1 text-sm text-muted-foreground">
            You're training as a guest — progress is kept on this device only.
            <span className="text-foreground"> Sign in to save streaks, mastery and daily results across devices.</span>
          </div>
          <Link to="/auth" data-testid="guest-banner-signin" className="bg-primary text-primary-foreground px-5 py-2 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-primary/90 whitespace-nowrap">
            Sign in to save
          </Link>
        </div>
      )}

      <div className={`panel p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 ${isStudy ? "border-sky-400/40" : ""}`} data-testid="modality-panel">
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-1">MODALITY · APPLIES TO MEDS, EKG, DAILY & CLAN CHALLENGES</div>
          <div className="text-sm">
            <span className={`font-bold ${isStudy ? "text-sky-300" : "text-phosphor"}`} data-testid="modality-current">{current.label}</span>
            <span className="text-muted-foreground"> — {current.desc}</span>
          </div>
        </div>
        <ModalitySwitch />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <StatTile icon={Flame} label="Current Streak" value={streak} unit="days" testid="stat-streak" />
        <StatTile icon={Target} label="Med Best · Hardcore" value={bestMed} unit={`easy ${progress?.stats?.med_best_score_easy || 0}`} testid="stat-med-score" />
        <StatTile icon={Activity} label="EKG Level" value={highestLevel} unit="/ 6" testid="stat-ekg-level" />
      </div>

      <WeeklyRecap />

      <div className="grid lg:grid-cols-2 gap-6">
        <ModeCard
          to="/medication-game"
          icon={Pill}
          title="Medication Drill"
          subtitle="Falling clue game • spaced repetition"
          body="Pick a drug class and a number of sets. Clues drop; you type the drug name before they hit the floor. Lose all points → session ends."
          testid="mode-medication"
        />
        <ModeCard
          to="/ekg-trainer"
          icon={Activity}
          title="EKG Trainer"
          subtitle="Single-lead → 12-lead • live monitor"
          body="Progress from sinus rhythms up through STEMI localization. Mute toggle. Beeps per QRS. Alarms for lethal rhythms."
          testid="mode-ekg"
        />
        <ModeCard
          to="/daily"
          icon={CalendarDays}
          title="Daily Challenge"
          subtitle="One shot per day • timed mix • shareable card"
          body="Ten shuffled items — five medication clues, five live rhythms — 12 seconds each. Finish and download a branded score card to share."
          testid="mode-daily"
        />
        <ModeCard
          to="/flashcards"
          icon={Layers}
          title="Study Flashcards"
          subtitle={`Quiet review • ${missedCount} item${missedCount === 1 ? "" : "s"} to revisit`}
          body="No timer, no score. Flip through the meds and rhythms you've missed, read the explanations, and mark them learned."
          testid="mode-flashcards"
        />
        <ModeCard
          to="/teams"
          icon={Users}
          title="Teams"
          subtitle="Clans • challenges • clan vs clan"
          body="Found or join a clan of up to 25. The owner sets a challenge — say, Paramedic Challenge this week — and every member's best score feeds the ranking."
          testid="mode-teams"
        />
        <ModeCard
          to="/avatars"
          icon={Coins}
          title="Avatar Shop"
          subtitle="Earn coins • recruit characters"
          body="Every exercise pays 1 coin per 100 points. Recruit chibi medics like Private Garcia and Nurse Atropine and equip them as your avatar."
          testid="mode-avatars"
        />
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, unit, testid }) {
  return (
    <div data-testid={testid} className="panel p-5 flex items-center gap-4">
      <Icon className="w-7 h-7 text-phosphor" />
      <div>
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase">{label}</div>
        <div className="text-2xl font-bold">
          {value}
          {unit && <span className="text-sm text-muted-foreground ml-2">{unit}</span>}
        </div>
      </div>
    </div>
  );
}

function ModeCard({ to, icon: Icon, title, subtitle, body, testid }) {
  return (
    <Link to={to} data-testid={testid} className="panel p-7 group hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between mb-6">
        <Icon className="w-10 h-10 text-phosphor" />
        <span className="text-[10px] tracking-[0.3em] text-muted-foreground group-hover:text-phosphor transition-colors">
          LAUNCH →
        </span>
      </div>
      <h2 className="text-2xl font-bold tracking-tight mb-1">{title}</h2>
      <div className="text-xs tracking-wider text-muted-foreground mb-4">{subtitle}</div>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </Link>
  );
}
